import { useRef, useState } from 'react';
import {
  Color,
  Size,
  confidentialShareProblems,
  permissionLadder,
  t,
  type ReleasePermissionValue,
  type SearchFn,
  type StringKey,
  type SuggestionRow,
} from '@bms2/shared';
import { Button, Checkbox, DatePicker, Dropdown, Pill, TextInput } from '../controls';
import { Modal, ModalField } from '../modals';
import { Text } from '../Text';
import { reportCaughtInBrowser } from '../../shell/systemErrors';
import styles from './ShareFileModal.module.css';

/**
 * The Share flow (piece 9, decisions 0085, 0191): one modal in which the
 * delivery choice is the create action — product owner, "combine the modals".
 * Recipient, permission and expiry are answered first; then Email, Copy or
 * Show QR each creates the link and delivers it in one motion, with no
 * separate Create Link step and no route that leaves without a record.
 *
 * The permission is a ladder and not independent checkboxes (decision 0085):
 * View is the floor and stays ticked, and ticking Download ticks and disables
 * it, since download-but-not-view is not a state a link can hold. Unticking
 * Download narrows back to View-only without leaving the modal. No route
 * offers Edit: a share link grants a read and the proxy declares no write.
 *
 * A Confidential file needs a named recipient and an expiry before any route
 * is offered, and the modal names both rather than refusing without saying so.
 */
export const shareFileModalDocumentedStates = ['closed', 'open', 'confidential', 'delivered'] as const;

export type ShareRoute = 'Email' | 'Copy' | 'Qr';

export interface ShareDraft {
  permission: ReleasePermissionValue;
  recipientPersonId: string | null;
  expiresAt: string | null;
  email: string | null;
}

export interface DeliveredShare {
  route: ShareRoute;
  url: string;
  qrDataUrl?: string;
}

export interface SharePerson {
  id: string;
  name: string;
  email: string | null;
}

export interface ShareFileModalProps {
  open: boolean;
  file: { id: string; filename: string; confidential: boolean };
  /**
   * 🔒 The people whose names match what the sender typed, asked of the server
   * each time (piece 9; T539). The modal holds no list of its own: a first page
   * of 200 read once left everybody after it unreachable.
   */
  searchPeople: (query: string, signal: AbortSignal) => Promise<readonly SharePerson[]>;
  defaultPermission?: ReleasePermissionValue;
  onDeliver: (route: ShareRoute, draft: ShareDraft) => Promise<{ url: string; qrDataUrl?: string }>;
  onCancel: () => void;
  openerId?: string | null;
  /** A delivery to open on, for the harness's named state. */
  initialDelivery?: DeliveredShare | null;
}

const ROUTE_LABELS: Record<ShareRoute, StringKey> = { Email: 'share.email', Copy: 'share.copy', Qr: 'share.qr' };
const ROUTE_DONE: Record<ShareRoute, StringKey> = { Email: 'share.deliveredEmail', Copy: 'share.deliveredCopy', Qr: 'share.deliveredQr' };
const ROUTES: readonly ShareRoute[] = ['Email', 'Copy', 'Qr'];
const PROBLEM_LABELS: Record<string, StringKey> = { recipientPersonId: 'share.needsRecipient', expiresAt: 'share.needsExpiry' };

/** The clipboard where the browser offers one; the link is shown in the field either way. */
function writeToClipboard(url: string): void {
  const clipboard = typeof navigator === 'undefined' ? null : navigator.clipboard;
  if (!clipboard || typeof clipboard.writeText !== 'function') return;
  void clipboard.writeText(url).catch(/* silent: a browser refusing the clipboard leaves the link on the screen to copy by hand. */ () => undefined);
}

export function ShareFileModal({
  open,
  file,
  searchPeople,
  defaultPermission = 'View',
  onDeliver,
  onCancel,
  openerId = null,
  initialDelivery = null,
}: ShareFileModalProps) {
  const [download, setDownload] = useState(defaultPermission === 'Download');
  const [recipient, setRecipient] = useState<SharePerson | null>(null);
  const [expiresAt, setExpiresAt] = useState('');
  const [typedEmail, setTypedEmail] = useState('');
  const [working, setWorking] = useState<ShareRoute | null>(null);
  const [delivered, setDelivered] = useState<DeliveredShare | null>(initialDelivery);

  const ladder = permissionLadder(download);
  const permission: ReleasePermissionValue = ladder.download ? 'Download' : 'View';
  const problems = confidentialShareProblems(
    { confidentialityRoleId: file.confidential ? file.id : null },
    { recipientPersonId: recipient?.id ?? null, expiresAt: expiresAt === '' ? null : new Date(expiresAt) },
  );
  const address = (recipient?.email ?? '') || typedEmail.trim();
  const draft: ShareDraft = {
    permission,
    recipientPersonId: recipient?.id ?? null,
    expiresAt: expiresAt === '' ? null : expiresAt,
    email: address === '' ? null : address,
  };

  const blocked = problems.length > 0 || working !== null;
  const routeDisabled = (route: ShareRoute): boolean => blocked || (route === 'Email' && address === '');

  /** The people each answer named, so a chosen row resolves to its address without a second read. */
  const seen = useRef(new Map<string, SharePerson>());
  const search: SearchFn = async (query, signal) => {
    const found = await searchPeople(query, signal);
    for (const person of found) seen.current.set(person.id, person);
    return found.map<SuggestionRow>((person) => ({ id: person.id, line1: person.name, ...(person.email === null ? {} : { line2: person.email }) }));
  };

  const deliver = (route: ShareRoute) => {
    if (routeDisabled(route)) return;
    setWorking(route);
    void onDeliver(route, draft).then(
      (result) => {
        setWorking(null);
        setDelivered({ route, url: result.url, ...(result.qrDataUrl === undefined ? {} : { qrDataUrl: result.qrDataUrl }) });
        if (route === 'Copy') writeToClipboard(result.url);
      },
      (error: unknown) => {
        setWorking(null);
        reportCaughtInBrowser('ShareFileModal.deliver', error);
      },
    );
  };

  const problemList = problems.map((problem) => t(PROBLEM_LABELS[problem] ?? 'share.recipient')).join(' · ');
  const needsMessage = `${t('share.confidentialNeeds')} ${problemList}`;
  const workingLabel = working === null ? '' : `${t('share.working')}: ${t(ROUTE_LABELS[working])}`;

  return (
    <Modal
      open={open}
      title={t('share.title')}
      subtitle={file.filename}
      onCancel={onCancel}
      openerId={openerId}
      dataModal="share"
      actions={[
        { kind: 'primary', label: t('share.done'), onAct: onCancel, disabled: delivered === null },
        { kind: 'cancel', label: t('modal.cancel'), onAct: onCancel },
      ]}
    >
      <div className={styles.body} data-share-permission={permission} data-share-confidential={file.confidential ? 'true' : 'false'}>
        {file.confidential ? <Pill text={t('pill.confidential')} foreground="var(--status-warning-text)" background="var(--status-warning-bg)" /> : null}

        {delivered === null ? (
          <>
            <Dropdown size={Size.Medium}
              label={t('share.recipient')}
              entityLabel={t('share.recipientEntity')}
              placeholder={t('share.recipientPlaceholder')}
              search={search}
              required={file.confidential}
              value={recipient?.name ?? null}
              onSelect={(row) => setRecipient(seen.current.get(row.id) ?? null)}
              // Nobody is created from here: Add New is the way to share with
              // somebody who holds no Person row, which is the typed address.
              onAddNew={() => setRecipient(null)}
            />

            <ModalField label={t('share.permission')}>
              <div className={styles.ladder} data-share-ladder="true">
                {/* View is the floor of the ladder and never comes off: a link
                    that grants nothing is a link nobody creates, so the way to
                    grant nothing is Cancel. */}
                <Checkbox label={t('share.view')} checked={ladder.view} disabled={ladder.viewDisabled} onChange={() => undefined} />
                <Checkbox label={t('share.download')} checked={ladder.download} onChange={setDownload} />
              </div>
              {ladder.download ? null : <Text as="p" variant="caption">{t('share.viewOnlyNote')}</Text>}
            </ModalField>

            <DatePicker size={Size.Small} label={t('share.expires')} value={expiresAt} onChange={setExpiresAt} required={file.confidential} />
            {/* A blank expiry takes files.shareLinkExpiryDays at creation (decision 0345); a Confidential file still needs an explicit one. */}
            {file.confidential ? null : <Text as="p" variant="caption">{t('share.expiryDefault')}</Text>}

            <TextInput size={Size.Medium} label={t('share.address')} type="email" value={recipient?.email ?? typedEmail} onChange={setTypedEmail} disabled={recipient?.email != null} />

            {problems.length > 0 ? (
              <p className={styles.needs} role="alert" data-share-problems={problems.join(' ')}>
                {needsMessage}
              </p>
            ) : null}
            {address === '' ? <Text as="p" variant="caption">{t('share.needsAddress')}</Text> : null}

            <Text as="p" variant="caption">{t('share.loginNote')}</Text>

            <div className={styles.routes} data-share-routes="true">
              <Text as="span" variant="caption">{t('share.deliver')}</Text>
              {ROUTES.map((route) => (
                <span key={route} data-share-deliver={route}>
                  <Button
                    size={Size.Medium}
                    color={route === 'Copy' ? Color.Navy : Color.Inactive}
                    text={[t(ROUTE_LABELS[route])]}
                    actions={[() => deliver(route)]}
                    disabled={routeDisabled(route)}
                  />
                </span>
              ))}
            </div>
            {working !== null ? <Text as="p" variant="caption">{workingLabel}</Text> : null}
          </>
        ) : (
          <div className={styles.done} data-share-route={delivered.route}>
            <Text as="p" variant="body">{t(ROUTE_DONE[delivered.route])}</Text>
            <ModalField label={t('share.link')}>
              <input className={styles.url} type="text" readOnly value={delivered.url} aria-label={t('share.link')} data-share-url={delivered.url} />
            </ModalField>
            {delivered.qrDataUrl ? <img className={styles.qr} src={delivered.qrDataUrl} alt={t('share.qrAlt')} data-share-qr="true" /> : null}
            {/* 🔨 What the recipient meets, said where the sender is looking (decision 0365).
                Every link needs a login and this modal cannot tell whether the
                recipient holds one, so it says the rule rather than guessing, and
                names both ways out: invite them, or let them ask from the link. */}
            <p className={styles.needsLogin} data-share-needs-login="true">{t('share.needsLoginNote')}</p>
          </div>
        )}
      </div>
    </Modal>
  );
}
